import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { Router } from "@angular/router";
import {
    BehaviorSubject,
    forkJoin,
    merge,
    Observable,
    Subject,
} from "rxjs";
import {
    debounceTime,
    map,
    shareReplay,
    switchMap,
    take,
    takeUntil,
} from "rxjs/operators";

import {
    GetActiveOrderQuery,
    RemoveItemFromCartMutation,
    RemoveItemFromCartMutationVariables,
} from "../../../common/generated-types";
import { orderHasStockIssues } from "../../../common/utils/cart-stock.utils";
import {
    cartHasConsolidatedFeedLines,
    getConsolidatedFeedCartTotal,
    getConsolidatedFeedMovRemaining,
    isConsolidatedFeedLine,
    isConsolidatedFeedMovMet,
} from "../../../common/utils/consolidated-feed-cart";
import { CHECKOUT_PATH, CHECKOUT_PAYMENT_PATH } from "../../../common/constants";
import { ORDER_STATE } from "../../../shared/constants/order.constants";
import {
    SupplierTotal,
    getSupplierReadiness,
    groupOrderLinesBySupplier,
    isSupplierReadyForCheckout,
} from "../../../shared/utils/cart.utils";
import { ConsolidatedFeedStorefrontService } from "../../../common/services/consolidated-feed-storefront.service";
import { DataService } from "../../providers/data/data.service";
import { NotificationService } from "../../providers/notification/notification.service";
import { StateService } from "../../providers/state/state.service";
import { ActiveService } from "../../providers/active/active.service";
import { CartUploadService } from "../../providers/cart-upload/cart-upload.service";

import {
    ADJUST_ITEM_QUANTITY,
    REMOVE_ALL_ITEMS,
    REMOVE_ITEM_FROM_CART,
} from "./cart.graphql";
import {
    CART_DEBOUNCE_MS,
    CART_MESSAGES,
    CART_REFETCH_QUERIES,
    CART_REMOVE_SUCCESS_RELOAD_DELAY_MS,
} from "./cart.constants";
import {
    CartAdjustOrderLineQuantityResult,
    CartQuantityChangeEvent,
    CartUploadSuccessPayload,
    RemoveAllItemsFromOrderMutationResult,
} from "./cart.types";

@Component({
    selector: "vsf-cart-page",
    templateUrl: "./cart.component.html",
    styleUrls: ["./cart.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartComponent implements OnInit, OnDestroy {
    @Input() visible = true;

    cart$: Observable<GetActiveOrderQuery["activeOrder"]>;
    isEmpty$: Observable<boolean>;
    isReadyForCheckout$ = new BehaviorSubject<boolean>(false);
    isImporting = false;
    isModalVisible = false;
    failedProductGtins: string[] = [];
    moqViolations: Array<{ gtin: string; moq: number; quantity: number }> = [];
    isLoading = true;

    readyTotal = 0;
    readySuppliersCount = 0;
    belowMinimumSuppliersCount = 0;
    moqViolationsCount = 0;
    isConsolidatedFeedCartView = false;
    consolidatedFeedMovRemaining = 0;
    optimizerModalOpen = false;
    optimizeGoal: "mov" | "value" = "mov";
    private excludedSupplierIds = new Set<string>();

    private readonly destroy$ = new Subject<void>();
    private readonly supplierTotalsCache = new WeakMap<
        object,
        Record<string, SupplierTotal>
    >();

    constructor(
        private dataService: DataService,
        private stateService: StateService,
        private activeService: ActiveService,
        private notificationService: NotificationService,
        private consolidatedFeedStorefront: ConsolidatedFeedStorefrontService,
        private cartUploadService: CartUploadService,
        private cd: ChangeDetectorRef,
        private router: Router
    ) {}

    ngOnInit() {
        this.consolidatedFeedStorefront
            .ensureLoaded()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.cd.markForCheck());

        this.cart$ = merge(
            this.stateService.select((state) => state.activeOrderId),
            this.stateService.select((state) => state.signedIn)
        ).pipe(
            switchMap(() => this.activeService.activeOrder$),
            shareReplay(1)
        );

        this.isEmpty$ = this.cart$.pipe(
            map((cart) => !cart?.lines?.length)
        );

        this.cart$
            .pipe(debounceTime(CART_DEBOUNCE_MS), takeUntil(this.destroy$))
            .subscribe({
                next: (cart) => {
                    this.isLoading = false;
                    if (cart) {
                        this.calculateReadyTotals(cart);
                        if (cart.state === ORDER_STATE.ARRANGING_PAYMENT) {
                            this.router.navigate([`/${CHECKOUT_PATH}`]);
                        }
                    }
                    this.cd.markForCheck();
                },
                error: () => {
                    this.isLoading = false;
                    this.cd.markForCheck();
                },
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private getSupplierTotals(
        cart: GetActiveOrderQuery["activeOrder"]
    ): Record<string, SupplierTotal> {
        if (!cart || !cart.lines) {
            return {};
        }
        const regularLines = cart.lines.filter(
            (line) => !isConsolidatedFeedLine(line),
        );
        if (!regularLines.length) {
            return {};
        }
        const cached = this.supplierTotalsCache.get(cart);
        if (cached) {
            return cached;
        }
        const totals = groupOrderLinesBySupplier(regularLines);
        this.supplierTotalsCache.set(cart, totals);
        return totals;
    }

    private calculateReadyTotals(cart: GetActiveOrderQuery["activeOrder"]): void {
        this.isConsolidatedFeedCartView = cartHasConsolidatedFeedLines(
            cart?.lines,
        );

        const supplierTotals = this.getSupplierTotals(cart);
        const { readyTotal, readyCount, belowCount } =
            getSupplierReadiness(supplierTotals);

        this.readyTotal = this.isConsolidatedFeedCartView
            ? getConsolidatedFeedCartTotal(cart?.lines) + readyTotal
            : readyTotal;
        this.readySuppliersCount = readyCount;
        this.belowMinimumSuppliersCount = belowCount;
        const consolidatedFeedMovCents =
            this.consolidatedFeedStorefront.getCheckoutMovCents();
        this.consolidatedFeedMovRemaining = this.isConsolidatedFeedCartView
            ? getConsolidatedFeedMovRemaining(
                  cart?.lines,
                  consolidatedFeedMovCents,
              )
            : 0;
        if (
            this.isConsolidatedFeedCartView &&
            !isConsolidatedFeedMovMet(cart?.lines, consolidatedFeedMovCents)
        ) {
            this.belowMinimumSuppliersCount += 1;
        }

        let moqViolations = 0;
        if (cart?.lines) {
            for (const line of cart.lines) {
                if (isConsolidatedFeedLine(line)) {
                    continue;
                }
                const pv = line.productVariant as any;
                const moq = pv?.pricingProduct?.minQ ?? pv?.moq ?? 1;
                if (line.quantity < moq) moqViolations++;
            }
        }
        this.moqViolationsCount = moqViolations;

        this.updateCheckoutReadiness(this.canProceedToCheckout(cart));
    }

    canProceedToCheckout(cart: GetActiveOrderQuery["activeOrder"]): boolean {
        if (!cart?.lines?.length) {
            return false;
        }
        if (this.hasStockIssues(cart)) {
            return false;
        }
        if (cartHasConsolidatedFeedLines(cart.lines)) {
            const consolidatedReady = isConsolidatedFeedMovMet(
                cart.lines,
                this.consolidatedFeedStorefront.getCheckoutMovCents(),
            );
            if (!consolidatedReady) {
                return false;
            }
            if (this.belowMinimumSuppliersCount > 0) {
                return false;
            }
            return this.allSuppliersReady(cart);
        }
        if (this.moqViolationsCount > 0) {
            return false;
        }
        if (this.belowMinimumSuppliersCount > 0) {
            return false;
        }
        return this.allSuppliersReady(cart);
    }

    async proceedToCheckout(cart: GetActiveOrderQuery["activeOrder"]) {
        if (!cart || !cart.lines) {
            return;
        }

        if (!this.canProceedToCheckout(cart)) {
            if (this.hasStockIssues(cart)) {
                this.notificationService
                    .error(CART_MESSAGES.STOCK_ISSUES_ERROR)
                    .subscribe();
            } else if (
                this.belowMinimumSuppliersCount > 0 ||
                (cartHasConsolidatedFeedLines(cart.lines) &&
                    !isConsolidatedFeedMovMet(
                        cart.lines,
                        this.consolidatedFeedStorefront.getCheckoutMovCents(),
                    ))
            ) {
                this.notificationService
                    .error(
                        cartHasConsolidatedFeedLines(cart.lines) &&
                            this.belowMinimumSuppliersCount === 0
                            ? CART_MESSAGES.CONSOLIDATED_FEED_MOV_NOT_MET_ERROR
                            : CART_MESSAGES.MOV_NOT_MET_ERROR,
                    )
                    .subscribe();
            }
            return;
        }

        try {
            this.isLoading = true;
            this.cd.markForCheck();

            const supplierTotals = this.getSupplierTotals(cart);
            const { unreadyLineIds } =
                getSupplierReadiness(supplierTotals);

            if (unreadyLineIds.length > 0) {
                this.notificationService
                    .error(CART_MESSAGES.MOV_NOT_MET_ERROR)
                    .subscribe();
                return;
            }

            this.router.navigate([`/${CHECKOUT_PATH}`, CHECKOUT_PAYMENT_PATH]);
        } catch (error) {
            console.error("Proceed to checkout error:", error);
            this.notificationService
                .error(CART_MESSAGES.CHECKOUT_PREPARE_ERROR)
                .subscribe();
            this.cd.markForCheck();
        } finally {
            this.isLoading = false;
            this.cd.markForCheck();
        }
    }

    setQuantity(event: CartQuantityChangeEvent) {
        if (0 < event.quantity) {
            this.adjustItemQuantity(event.itemId, event.quantity);
        } else {
            this.removeItem(event.itemId);
        }
    }

    allLinesMeetMOV(cart: GetActiveOrderQuery["activeOrder"]): boolean {
        return this.allSuppliersReady(cart);
    }

    allSuppliersReady(cart: GetActiveOrderQuery["activeOrder"]): boolean {
        const supplierTotals = this.getSupplierTotals(cart);

        const totalsArray = Object.values(supplierTotals);
        if (totalsArray.length === 0) {
            return true;
        }

        return totalsArray.every((s) => isSupplierReadyForCheckout(s));
    }

    hasStockIssues(cart: GetActiveOrderQuery["activeOrder"]): boolean {
        return orderHasStockIssues(cart);
    }

    updateCheckoutReadiness(isReady: boolean): void {
        this.isReadyForCheckout$.next(isReady);
    }

    onIncludedChange(excludedIds: string[]): void {
        this.excludedSupplierIds = new Set(excludedIds);
        this.cart$.pipe(take(1)).subscribe((cart) => {
            if (cart) {
                this.calculateReadyTotals(cart);
                this.cd.markForCheck();
            }
        });
    }

    getUnitCount(cart: GetActiveOrderQuery["activeOrder"]): number {
        return (cart?.lines ?? []).reduce((sum, line) => sum + line.quantity, 0);
    }

    getSupplierCount(cart: GetActiveOrderQuery["activeOrder"]): number {
        return Object.keys(this.getSupplierTotals(cart)).length +
            (cartHasConsolidatedFeedLines(cart?.lines) ? 1 : 0);
    }

    getVolumeSavings(cart: GetActiveOrderQuery["activeOrder"]): number {
        return (cart?.lines ?? []).reduce((sum, line) => {
            const full = line.linePriceWithTax ?? 0;
            const discounted = line.discountedLinePriceWithTax ?? full;
            return sum + Math.max(0, full - discounted);
        }, 0);
    }

    getStatusLine(cart: GetActiveOrderQuery["activeOrder"]): string {
        if (this.canProceedToCheckout(cart)) {
            return `All ${this.getSupplierCount(cart)} suppliers meet their minimum — ready to checkout.`;
        }
        if (this.belowMinimumSuppliersCount > 0) {
            return `${this.belowMinimumSuppliersCount} supplier${
                this.belowMinimumSuppliersCount === 1 ? "" : "s"
            } below MOV. Increase quantities or add products to continue.`;
        }
        if (this.moqViolationsCount > 0) {
            return `MOQ not met for ${this.moqViolationsCount} SKU(s). Increase quantities to proceed.`;
        }
        if (this.hasStockIssues(cart)) {
            return "Some products are out of stock or exceed available stock.";
        }
        return "Review your cart before checkout.";
    }

    getCheckoutLabel(cart: GetActiveOrderQuery["activeOrder"]): string {
        if (!this.canProceedToCheckout(cart)) {
            return "Checkout unavailable";
        }
        const n = this.readySuppliersCount;
        return `Checkout ${n} supplier${n === 1 ? "" : "s"} →`;
    }

    getCheckoutHelper(cart: GetActiveOrderQuery["activeOrder"]): string {
        if (!cart?.lines?.length) {
            return "";
        }
        if (this.canProceedToCheckout(cart)) {
            return "All selected suppliers meet their minimum order value.";
        }
        if (this.belowMinimumSuppliersCount > 0) {
            return "Raise quantities or add products until every included supplier clears its MOV.";
        }
        return "Resolve stock or MOQ issues to continue.";
    }

    openOptimizerModal(): void {
        this.optimizerModalOpen = true;
        this.cd.markForCheck();
    }

    closeOptimizerModal(): void {
        this.optimizerModalOpen = false;
        this.cd.markForCheck();
    }

    applyOptimizer(): void {
        this.closeOptimizerModal();
        this.notificationService
            .info(
                "Cart optimizer suggestions are not available yet. Use Add products on each supplier to close the MOV gap."
            )
            .subscribe();
    }

    exportCart(cart: GetActiveOrderQuery["activeOrder"]): void {
        this.cartUploadService.exportCartToXlsx(cart?.lines ?? []);
    }

    openUploadModal() {
        this.isModalVisible = true;
        this.cd.markForCheck();
    }

    closeUploadModal() {
        this.isModalVisible = false;
        this.cd.markForCheck();
    }

    dismissStockReport(): void { this.cartUploadService.stockShortfalls = []; }

    get importStockShortfalls() { return this.cartUploadService.stockShortfalls; }

    onUploadSuccess(result: CartUploadSuccessPayload) {
        this.failedProductGtins = result.failedProductGtins;
        this.moqViolations = result.moqViolations || [];
        this.closeUploadModal();
    }

    private adjustItemQuantity(id: string, qty: number) {
        this.dataService
            .mutate<CartAdjustOrderLineQuantityResult>(ADJUST_ITEM_QUANTITY, {
                id,
                qty,
            })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(({ adjustOrderLineQuantity }) => {
                const { errorMessage } = adjustOrderLineQuantity;
                if (errorMessage) {
                    this.notificationService.error(errorMessage).subscribe();
                }
                this.dataService.refetchQueries([...CART_REFETCH_QUERIES]);
            });
    }

    async removeAllItems() {
        try {
            const result = await this.dataService
                .mutate<RemoveAllItemsFromOrderMutationResult>(REMOVE_ALL_ITEMS)
                .pipe(take(1))
                .toPromise();
            if (result?.removeAllItemsFromOrder) {
                this.notificationService
                    .success(CART_MESSAGES.REMOVED_ALL)
                    .subscribe();
                setTimeout(
                    () => window.location.reload(),
                    CART_REMOVE_SUCCESS_RELOAD_DELAY_MS
                );
            } else {
                this.notificationService
                    .error(CART_MESSAGES.REMOVE_ALL_FAILED)
                    .subscribe();
            }
        } catch (error) {
            console.error("Error: ", error);
            this.notificationService
                .error(CART_MESSAGES.REMOVE_ALL_ERROR)
                .subscribe();
        }
    }

    removeItem(id: string) {
        this.dataService
            .mutate<CartAdjustOrderLineQuantityResult>(REMOVE_ITEM_FROM_CART, {
                id,
            })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(({ adjustOrderLineQuantity }) => {
                const { errorMessage } = adjustOrderLineQuantity;
                if (errorMessage) {
                    this.notificationService.error(errorMessage).subscribe();
                }
                this.dataService.refetchQueries([...CART_REFETCH_QUERIES]);
            });
    }
}
